import React, { useRef, useState, useEffect } from 'react'
import { TimelinePost } from '@/types/post'
import { Badge } from '@/components/ui/badge'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface PostTagsProps {
  post: TimelinePost
}

export function PostTags({ post: _post }: PostTagsProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const hasContexts = true
  const hasTerritories = true
  const hasTopics = true
  const hasBrand = true
  const hasCommunity = true
  const topicsCount = 8

  const mockBadges = [
    { label: 'Contexto', show: hasContexts },
    { label: 'Marca', show: hasBrand },
    { label: 'Tópico', show: hasTopics, count: topicsCount },
    { label: 'Comunidade', show: hasCommunity },
    { label: 'Território', show: hasTerritories },
    { label: 'Contexto 2', show: true },
    { label: 'Marca 2', show: true },
    { label: 'Tópico Extra', show: true },
    { label: 'Comunidade 2', show: true },
    { label: 'Território 2', show: true },
    { label: 'Outro Contexto', show: true },
    { label: 'Outra Marca', show: true },
  ]

  const checkScrollability = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1)
    }
  }

  useEffect(() => {
    checkScrollability()
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', checkScrollability)
      window.addEventListener('resize', checkScrollability)
      return () => {
        container.removeEventListener('scroll', checkScrollability)
        window.removeEventListener('resize', checkScrollability)
      }
    }
  }, [])

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200
      const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount)
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      })
    }
  }

  return (
    <div className="relative pt-4 border-t border-gray-200 w-full">
      {canScrollLeft && (
        <Button
          variant="ghost"
          size="sm"
          className="absolute left-0 top-4 h-6 w-6 p-0 bg-white shadow-md hover:bg-gray-50 z-10"
          onClick={() => scroll('left')}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      )}
      <div
        ref={scrollContainerRef}
        className={`flex items-center gap-2 overflow-x-auto scrollbar-hide ${canScrollLeft ? 'pl-8' : ''} ${canScrollRight ? 'pr-8' : ''}`}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {mockBadges.map((badge, index) => {
          if (!badge.show) return null
          
          if (badge.count && badge.count > 2) {
            return (
              <React.Fragment key={index}>
                {Array.from({ length: Math.min(badge.count, 2) }).map((_, i) => (
                  <Badge key={`${index}-${i}`} variant="secondary" className="bg-gray-100 text-black text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                    {badge.label}
                  </Badge>
                ))}
                {badge.count > 2 && (
                  <Badge variant="secondary" className="bg-gray-100 text-black text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                    +{badge.count - 2}
                  </Badge>
                )}
              </React.Fragment>
            )
          }
          
          return (
            <Badge key={index} variant="secondary" className="bg-gray-100 text-black text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
              {badge.label}
            </Badge>
          )
        })}
      </div>
      {canScrollRight && (
        <Button
          variant="ghost"
          size="sm"
          className="absolute right-0 top-4 h-6 w-6 p-0 bg-white shadow-md hover:bg-gray-50 z-10"
          onClick={() => scroll('right')}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
